import { Fragment, useState } from "react";
import PropTypes from "prop-types";
import LabelForm from "pages/signup/form/labelForm";
import { useTranslation } from "react-i18next";
import Input from "components/input";
import RadioButtons from "components/radioButtons";
import UploadFile from "components/uploadFile";
import { ReactComponent as AttachIcon } from "images/auth/attach.svg";
import useUploadFiles from "pages/signup/hooks/useUploadFiles";
import Select from "components/select";
import {
  vatCertificationType,
  SUBSTATUS_ONBOARDING_REQUEST,
} from "utils/constantValues";

function VatExempt({
  countries = [],
  register = () => {},
  watch = () => {},
  setValue = () => {},
  errors,
  userDetails = {},
}) {
  const vatCertification = userDetails?.userFiles?.find(
    (file) => file.type === vatCertificationType
  );

  const isOnboardingRequest =
    userDetails?.subStatus === SUBSTATUS_ONBOARDING_REQUEST;

  const [t] = useTranslation();
  const isVatExempt = watch("isVatExempt");
  const [radios, setRadios] = useState([
    {
      title: t("Yes"),
      isActive: userDetails?.flags?.isVatExempt,
    },
    { title: t("No"), isActive: !userDetails?.flags?.isVatExempt },
  ]);
  const countriesOptions = countries.map((elem) => ({
    ...elem,
    label: elem.name,
    value: elem.id,
  }));

  const {
    vatRef,
    isUploading,
    vatFileName,
    uploadVatCertificate,
    removeVatCertificate,
  } = useUploadFiles({});

  return (
    <Fragment>
      <div className="flex">
        <LabelForm
          label={t("vatExemptApplicable")}
          labelClassName="text-primary font-bold text-[24px] md-max:text-[18px] not-italic pt-[16px] bg-white z-[3]"
        />
      </div>

      <div className="grid grid-cols-2 md-max:grid-cols-1 gap-[16px] max-w-[843px] sm-max:max-w-full pt-[10px] px-[0px] pb-[30px]  z-[3] bg-white border-b-[1px] border-solid border-default-color">
        <RadioButtons
          label={t("vatExempt")}
          contents={radios}
          onClick={(_, index) => {
            setValue("isVatExempt", !isVatExempt);
            const updatedContents = radios.map((elem, i) => {
              return { ...elem, isActive: i === index };
            });

            setRadios(updatedContents);
          }}
        />
        {!isVatExempt && (
          <>
            <Select
              label={t("vatCountry")}
              placeholder={t("placeholderSelect", {
                field: t("vatCountry").toLowerCase(),
              })}
              name={"vatCountryId"}
              isClearable={false}
              containerClassName="flex flex-1 flex-col gap-[10px] min-w-[250px] sm-max:w-full"
              value={
                watch("vatCountryId")
                  ? countriesOptions.find(
                      (elem) => elem.id === watch("vatCountryId")
                    )
                  : null
              }
              onChange={(e) => {
                setValue("vatCountryId", e.value);
              }}
              isRequired={true}
              register={register}
              registerArgs={{
                required: t("errorField", { field: t("vatCountry") }),
              }}
              errorMessage={
                errors.vatCountryId ? errors.vatCountryId.message : ""
              }
              options={countriesOptions}
            />

            <Input
              label={t("vatRegistrationNumber")}
              placeholder={t("placeHolderType", {
                title: t("vatRegistrationNumber").toLowerCase(),
              })}
              type="string"
              name="vatRegistrationNumber"
              register={register}
              isRequired={true}
              registerArgs={{
                required: t("errorField", {
                  field: t("vatRegistrationNumber"),
                }),
              }}
              errorMessage={
                errors.vatRegistrationNumber
                  ? errors.vatRegistrationNumber.message
                  : ""
              }
              containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
              labelClassName="text-primary font-semibold text-[16px]"
              inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                errors.vatRegistrationNumber
                  ? "border-red"
                  : "border-default-color"
              } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
            />

            {(isOnboardingRequest || !vatCertification) && (
              <UploadFile
                name={"vatCertification"}
                register={register}
                registerArgs={{
                  required: vatFileName
                    ? false
                    : t("errorField", { field: t("vatCertification") }),
                }}
                label={t("vatCertification")}
                placeHolderLabel={t("attachUrVat")}
                icon={<AttachIcon />}
                isUploading={isUploading.vatCertification}
                getFile={(e, isDrag) =>
                  uploadVatCertificate(e, isDrag, setValue)
                }
                labelClassName="text-primary font-semibold text-[16px]"
                placeHolderContainerClassName={`flex p-[14px] justify-between items-center gap-[8px] self-stretch rounded-[6px] h-[43px] w-full min-w-[190px] sm-max:min-w-full sm-max:w-full border-[1px] border-dashed ${
                  errors.vatCertification && !vatFileName
                    ? "border-red"
                    : "border-dark-gray"
                } cursor-pointer`}
                isRequired={true}
                errorMessage={
                  errors.vatCertification ? errors.vatCertification.message : ""
                }
                ref={vatRef}
                fileName={vatFileName}
                onRemoveFile={(fileName) =>
                  removeVatCertificate(fileName, setValue)
                }
              />
            )}
          </>
        )}
      </div>
    </Fragment>
  );
}

VatExempt.propTypes = {
  countries: PropTypes.array,
  register: PropTypes.func,
  watch: PropTypes.func,
  setValue: PropTypes.func,
  errors: PropTypes.object,
  userDetails: PropTypes.object,
};

export default VatExempt;
